import React, { useState, useEffect } from 'react';
import { X, Cookie } from 'lucide-react';
import { Button } from './ui/button';
import { getConsent, acceptAll, rejectNonEssential } from '../utils/cookieConsent';
import CookieSettings from './CookieSettings';

const CookieBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    // Check if user has already given consent
    const consent = getConsent();
    if (!consent) {
      // Show banner after a small delay for better UX
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    acceptAll();
    setIsVisible(false);
  };

  const handleRejectNonEssential = () => {
    rejectNonEssential();
    setIsVisible(false);
  };

  const handleCustomize = () => {
    setShowSettings(true);
  };

  const handleCloseSettings = () => {
    setShowSettings(false);
    setIsVisible(false);
  };

  if (!isVisible && !showSettings) return null;

  if (showSettings) {
    return <CookieSettings onClose={handleCloseSettings} />;
  }

  return (
    <div
      className="fixed bottom-4 left-4 right-4 sm:right-auto sm:max-w-sm z-[100] animate-slide-up"
      role="dialog"
      aria-live="polite"
      aria-label="Consentimento de cookies"
      data-testid="cookie-banner"
    >
      <div className="bg-white rounded-xl shadow-xl border border-gray-200 p-4 backdrop-blur-sm">
        {/* Header */}
        <div className="flex items-start gap-3 mb-3">
          <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <Cookie className="text-orange-600" size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-gray-900 leading-tight">
              Cookies
            </h3>
            <p className="text-xs text-gray-600 leading-relaxed mt-1">
              Usamos cookies essenciais e, com a sua permissão, analíticos para melhorar o site.{' '}
              <a href="/politica-cookies" className="text-orange-600 hover:text-orange-700 underline">
                Saber mais
              </a>
            </p>
          </div>
          <button
            onClick={handleRejectNonEssential}
            className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0 -mt-1 -mr-1 p-1"
            aria-label="Rejeitar cookies não essenciais e fechar"
            data-testid="cookie-close-btn"
          >
            <X size={16} />
          </button>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <Button
            onClick={handleAcceptAll}
            className="flex-1 bg-orange-600 hover:bg-orange-700 text-white text-xs font-medium h-8 px-3"
            data-testid="cookie-accept-all"
          >
            Aceitar
          </Button>
          <Button
            onClick={handleRejectNonEssential}
            variant="outline"
            className="flex-1 border-gray-300 text-gray-700 hover:bg-gray-50 text-xs font-medium h-8 px-3"
            data-testid="cookie-reject"
          >
            Rejeitar
          </Button>
          <button
            onClick={handleCustomize}
            className="text-xs text-gray-500 hover:text-orange-600 underline transition-colors px-2 h-8 flex items-center"
            data-testid="cookie-customize"
          >
            Opções
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
